/**
 * Parse, type-infer, and re-assemble multi-part question payloads — entirely
 * client-side. The backend's `multi_part` schema carries no per-part labels
 * or types; the structure lives inline in the question text via `(a)/(b)/...`
 * markers (when present). Inference is conservative: any question without
 * clear markers is reported as `parsed: null` and the caller should fall back
 * to the existing single-textarea behavior.
 *
 * No backend change: the per-part answers reassemble into the same
 * `"(a) X; (b) Y; (c) Z"` shape the answer_key uses, so the existing model
 * grader (backend/grader.py:_model_grade) sees what it has always seen.
 */

export type PartInputKind =
  | { kind: "numeric" }
  | { kind: "text" }
  // Binary choice — covers explicit "ANSWER YES or NO" style prompts, plus
  // ACCEPT or REJECT, MAKE or BUY, KEEP or DROP — anything that reads as a
  // two-token capitalized binary in the part text.
  | { kind: "binary"; options: [string, string] }
  // N-option choice (2-4 options) — used by the Flavor 2 parts-array path
  // where the generator authored typed choice parts with explicit options.
  // Inference paths use "binary"; declared (parts_json) paths use "choice".
  | { kind: "choice"; options: string[] };

export interface ParsedPart {
  /** "a" / "b" / "c" / "d" — lowercase letter that prefixed the part in the stem. */
  letter: string;
  /** The raw prompt text for this part (everything between `(a)` and `(b)`). */
  prompt: string;
  /** Conservative inference. `confidence: "known"` for binary (literal Y/N or
   *  KEEP/DROP-style match in prompt); `"inferred"` for numeric and text. */
  type: PartInputKind;
  confidence: "known" | "inferred";
}

export interface ParsedMultiPart {
  /** Everything before the first `(a)` marker — the scenario setup. */
  preamble: string;
  parts: ParsedPart[];
}

// `(a)` / `( a )` / case-insensitive — start of line OR preceded by a blank
// line OR whitespace. Greedy enough to catch the common patterns; tight enough
// not to grab `(ABC) Corp` or similar mid-prose tokens.
const MARKER_RE = /(^|\n\s*)\(([a-f])\)\s+/gi;

// Binary patterns — look for two uppercase words joined by " or ". The
// uppercase requirement is what stops false positives like "compute or
// calculate"; the question authors use full caps for the canonical
// student-choice tokens.
const BINARY_RE = /\b([A-Z]{2,})\s+or\s+([A-Z]{2,})\b/;

const NUMERIC_HINTS = [
  /\bcompute\b/i,
  /\bcalculate\b/i,
  /\bdetermine\b/i,
  /\bhow many\b/i,
  /\bwhat is the\b/i,
  /\bround to\b/i,
  // physical-quantity nouns that introduce a numeric answer
  /\b(amount|total|volume|count|number|sum|price|cost|revenue|income|loss|margin|profit|rate|ratio|breakeven|break-?even)\b/i,
  // explicit units of measure — strong numeric signal
  /\b(units?|liters?|gallons?|minutes?|hours?|seconds?|days?|months?|years?|tons?|pounds?|lb|kg|kilograms?|grams?|miles?|kilometers?|km|cm|mm|inches?|feet|ft|in\b)\b/i,
  // "(in cm³)" / "(in liters)" / "($ thousands)" — parenthetical unit hints
  /\(\s*in\s+\$?\w/i,
  /\bper\s+(unit|box|ton|tile|item|order|customer|piece|month|period|hour|day|year|week)\b/i,
  /\$/,
  /%/,
  /\b\d+(\.\d+)?\b/, // any literal number is a numeric signal
];

/**
 * Try to split a multi_part question stem into a preamble + per-part prompts.
 * Returns `null` if the stem doesn't carry enough markers (need ≥2 ordered
 * `(a)` … `(f)` markers).
 */
export function parseMultiPartStem(stem: string): ParsedMultiPart | null {
  if (!stem) return null;

  // Reset the global regex — Safari/old engines hold lastIndex across calls.
  MARKER_RE.lastIndex = 0;

  const matches: { letter: string; index: number; endOfMarker: number }[] = [];
  let m: RegExpExecArray | null;
  while ((m = MARKER_RE.exec(stem))) {
    matches.push({
      letter: m[2].toLowerCase(),
      // m.index points at the optional leading newline; we want the actual
      // position of "(letter)".
      index: m.index + m[1].length,
      endOfMarker: m.index + m[0].length,
    });
  }
  if (matches.length < 2) return null;

  // Sanity check: letters must be a prefix of "abcdef" in order. Otherwise
  // we're probably catching incidental "(a) foo bar" in scattered prose.
  const letters = matches.map((x) => x.letter);
  const expected = "abcdef".slice(0, matches.length).split("");
  for (let i = 0; i < letters.length; i++) {
    if (letters[i] !== expected[i]) return null;
  }

  const preamble = stem.slice(0, matches[0].index).trim();
  const parts: ParsedPart[] = matches.map((mm, i) => {
    const start = mm.endOfMarker;
    const end = i + 1 < matches.length ? matches[i + 1].index : stem.length;
    const prompt = stem.slice(start, end).trim();
    const [type, confidence] = inferPartType(prompt);
    return { letter: mm.letter, prompt, type, confidence };
  });

  return { preamble, parts };
}

/**
 * Best-effort per-part type. Conservative: when in doubt, return `text`
 * with confidence `inferred`.
 */
function inferPartType(prompt: string): [PartInputKind, "known" | "inferred"] {
  const bin = prompt.match(BINARY_RE);
  if (bin) {
    const a = bin[1];
    const b = bin[2];
    // Special-case YES/NO so the buttons read naturally even when the
    // prompt's other tokens happen to be uppercase (e.g. "YES or NO IF
    // applicable").
    if ((a === "YES" && b === "NO") || (a === "NO" && b === "YES")) {
      return [{ kind: "binary", options: ["YES", "NO"] }, "known"];
    }
    return [{ kind: "binary", options: [a, b] }, "known"];
  }
  for (const re of NUMERIC_HINTS) {
    if (re.test(prompt)) {
      return [{ kind: "numeric" }, "inferred"];
    }
  }
  return [{ kind: "text" }, "inferred"];
}

/**
 * Assemble per-part answers back into the same `"(a) X; (b) Y; ..."` shape
 * the answer_key uses on the server. The backend grader is model-graded and
 * charitable about format; this format is what it has always been trained
 * on for multi_part keys.
 */
export function composeMultiPartAnswer(
  parts: ParsedPart[],
  answers: string[],
): string {
  return parts
    .map((p, i) => {
      const raw = (answers[i] ?? "").trim();
      return `(${p.letter}) ${raw}`;
    })
    .join("; ");
}

/**
 * Every part must have a non-empty answer (binary parts count when one of the
 * two options is chosen).
 */
export function multiPartReady(
  parts: ParsedPart[],
  answers: string[],
): boolean {
  if (parts.length === 0) return false;
  return parts.every((_p, i) => (answers[i] ?? "").trim().length > 0);
}
